import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { toast } from "sonner";

interface Profile {
  id: string;
  full_name: string;
  avatar_url: string | null;
  bio: string;
  user_type: "student" | "parent";
  year_group: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isAdmin: boolean;
  isTutor: boolean;
  isParent: boolean;
  needsProfileCompletion: boolean;
  needsMfaVerify: boolean;
  mfaEnabled: boolean;
  signUp: (email: string, password: string, fullName: string, yearGroup?: string, userType?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshMfaStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTutor, setIsTutor] = useState(false);
  const [needsMfaVerify, setNeedsMfaVerify] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const congratsShownRef = useRef(false);

  const isParent = profile?.user_type === "parent";

  // Detect OAuth users who haven't completed profile (default "student" with empty year_group)
  const isGoogleUser = !!user?.identities?.some((id) => id.provider === "google");
  const needsProfileCompletion = !!user && !!profile && 
    isGoogleUser &&
    profile.user_type === "student" && 
    profile.year_group === "";

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("id, full_name, avatar_url, bio, user_type, year_group")
      .eq("id", userId)
      .single();
    setProfile(data as Profile | null);
  };

  const fetchRoles = async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    setIsAdmin(data?.some(r => r.role === "admin") ?? false);
    const nowTutor = data?.some(r => r.role === "tutor") ?? false;
    setIsTutor(nowTutor);

    // Show congrats toast for newly approved tutors
    if (nowTutor && !congratsShownRef.current) {
      const { data: approvalNotifs } = await supabase
        .from("notifications")
        .select("id")
        .eq("user_id", userId)
        .eq("type", "tutor_approved")
        .eq("read", false)
        .limit(1);

      if (approvalNotifs && approvalNotifs.length > 0) {
        congratsShownRef.current = true;
        setTimeout(() => {
          toast.success("Congratulations, you're a tutor! 🎉", {
            description: "Head to your portfolio to customise your profile and start receiving bookings.",
            duration: 120000,
          });
          supabase
            .from("notifications")
            .update({ read: true })
            .eq("id", approvalNotifs[0].id)
            .then(() => {});
        }, 2000);
      }
    }
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  const checkMfaStatus = async () => {
    const { data: factors } = await supabase.auth.mfa.listFactors();
    const hasVerifiedTotp = factors?.totp?.some((f: any) => f.status === "verified") ?? false;
    setMfaEnabled(hasVerifiedTotp);
    if (hasVerifiedTotp) {
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      setNeedsMfaVerify(aal?.currentLevel === "aal1" && aal?.nextLevel === "aal2");
    } else {
      setNeedsMfaVerify(false);
    }
  };

  const refreshMfaStatus = async () => {
    await checkMfaStatus();
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          setTimeout(async () => {
            const { data: bans } = await supabase
              .from("user_bans")
              .select("is_active, ban_type, expires_at, reason")
              .eq("user_id", session.user.id)
              .eq("is_active", true)
              .limit(1);
            if (bans && bans.length > 0) {
              const ban = bans[0];
              if (ban.ban_type === "temporary" && ban.expires_at) {
                const expiresAt = new Date(ban.expires_at);
                if (expiresAt <= new Date()) {
                  // Ban expired, allow access
                } else {
                  await supabase.auth.signOut();
                  const hoursLeft = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60));
                  sessionStorage.setItem("bookr_ban_info", JSON.stringify({
                    reason: ban.reason || "Violation of platform rules",
                    banType: ban.ban_type,
                    expiresAt: ban.expires_at,
                    hoursLeft,
                  }));
                  window.location.href = "/auth";
                  return;
                }
              } else {
                await supabase.auth.signOut();
                sessionStorage.setItem("bookr_ban_info", JSON.stringify({
                  reason: ban.reason || "Account terminated",
                  banType: ban.ban_type,
                  expiresAt: null,
                  hoursLeft: null,
                }));
                window.location.href = "/auth";
                return;
              }
            }
            fetchProfile(session.user.id);
            fetchRoles(session.user.id);
            checkMfaStatus();
            // Track login IP in background
            supabase.functions.invoke("track-login-ip", { body: { event_type: "login" } }).catch(() => {});
            // Only process referral/affiliate after email is verified
            const emailVerified = !!session.user.email_confirmed_at;
            if (emailVerified) {
              const refCode = localStorage.getItem("bookr_ref_code");
              if (refCode) {
                localStorage.removeItem("bookr_ref_code");
                supabase.functions.invoke("process-referral-signup", {
                  body: { referral_code: refCode, new_user_id: session.user.id },
                }).catch(() => {});
              }
              const affCode = localStorage.getItem("bookr_aff_code");
              const cookieAff = document.cookie.match(/bookr_aff=([^;]+)/)?.[1];
              const finalAffCode = affCode || (cookieAff ? decodeURIComponent(cookieAff) : "");
              if (finalAffCode) {
                localStorage.removeItem("bookr_aff_code");
                document.cookie = "bookr_aff=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
                supabase.functions.invoke("process-affiliate-signup", {
                  body: { affiliate_code: finalAffCode, user_id: session.user.id },
                }).catch(() => {});
              }
            }
          }, 0);
        } else {
          setProfile(null);
          setIsAdmin(false);
          setIsTutor(false);
        }
        setLoading(false);
      }
    );

    // getSession is only needed as a fallback if onAuthStateChange hasn't fired yet.
    // The listener above handles session restoration, so we just ensure loading is set to false.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        setLoading(false);
      }
      // If session exists, onAuthStateChange will handle it
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string, yearGroup?: string, userType?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { 
          full_name: fullName, 
          year_group: yearGroup || "",
          user_type: userType || "student",
        },
        emailRedirectTo: window.location.origin,
      },
    });
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setIsAdmin(false);
    setIsTutor(false);
    setMfaEnabled(false);
    setNeedsMfaVerify(false);
  };

  // Global redirect for incomplete Google OAuth profiles
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (needsProfileCompletion && !["/complete-profile", "/auth", "/terms", "/privacy"].includes(location.pathname)) {
      navigate("/complete-profile");
    }
  }, [needsProfileCompletion, location.pathname, navigate]);

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, isAdmin, isTutor, isParent, needsProfileCompletion, needsMfaVerify, mfaEnabled, signUp, signIn, signOut, refreshProfile, refreshMfaStatus }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
